import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { DocsDiagram, DocsDiagramTransition } from "../../models/Docs";

const edgeAt = (target: EventTarget | null): number | null => {
  const element = target instanceof Element ? target.closest("[data-edge]") : null;
  return element ? Number(element.getAttribute("data-edge")) : null;
};

/**
 * A server-rendered diagram and the numbered list of its transitions, grouped by what causes them. Pointing at an
 * arrow, its number or its row highlights the other two and dims every other transition; the arrow and badge are
 * matched through their `data-edge` attribute, because the SVG arrives as markup rather than React elements.
 */
const DiagramFigure: React.FC<{ diagram: DocsDiagram }> = ({ diagram }) => {
  const drawing = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    const root = drawing.current;
    if (!root) return;
    root.querySelectorAll(".is-on").forEach((element) => element.classList.remove("is-on"));
    if (active !== null) root.querySelectorAll(`[data-edge="${active}"]`).forEach((element) => element.classList.add("is-on"));
  }, [active, diagram.svg]);

  const single = diagram.groups.length === 1;
  const item = (kind: string, transition: DocsDiagramTransition) => (
    <li
      key={transition.number}
      data-edge={transition.number}
      tabIndex={0}
      title={`${transition.from} → ${transition.to}`}
      onFocus={() => setActive(transition.number)}
      onBlur={() => setActive(null)}
      className={cn(
        "dd-item flex break-inside-avoid gap-2 rounded-md px-1.5 py-1 text-sm leading-snug outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active === transition.number && "is-on",
      )}
    >
      <span
        aria-hidden
        className={cn("dd-number mt-px inline-grid size-[18px] shrink-0 place-items-center rounded-full text-[11px] font-semibold", `kind-${kind}`)}
      >
        {transition.number}
      </span>
      <span>
        <span className="sr-only">{transition.number}. </span>
        <span className="font-semibold">{transition.label}.</span>
        {transition.description && <> {transition.description}</>}
      </span>
    </li>
  );

  return (
    <figure
      className={cn("docs-figure not-prose my-6 rounded-lg border bg-card", active !== null && "dd-focus")}
      onMouseOver={(event) => setActive(edgeAt(event.target))}
      onMouseLeave={() => setActive(null)}
    >
      {/* Server-rendered from the page's own diagram block; every label is XML-escaped there. */}
      <div ref={drawing} className="overflow-x-auto p-4" dangerouslySetInnerHTML={{ __html: diagram.svg }} />
      {diagram.groups.length > 0 && (
        <figcaption className={cn("border-t px-4 py-3", !single && "sm:columns-2 sm:gap-6")}>
          {diagram.groups.map((group) => (
            <div key={group.kind} className="mb-3 break-inside-avoid last:mb-0">
              <div className="mb-1 flex items-center gap-2 px-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span aria-hidden className={cn("dd-swatch", `kind-${group.kind}`)} />
                {group.title}
              </div>
              <ol className={cn(single && "sm:columns-2 sm:gap-6")}>
                {group.transitions.map((transition) => item(group.kind, transition))}
              </ol>
            </div>
          ))}
        </figcaption>
      )}
    </figure>
  );
};

export default DiagramFigure;
